import { CheckIcon, ChevronUp } from "lucide-react";
import * as React from "react";
import * as RPNInput from "react-phone-number-input";
import flags from "react-phone-number-input/flags";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Input, InputProps } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export let myCountry: RPNInput.Country;

const InputComponent = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, ...props }, ref) => (
    <Input
      className={cn("rounded-s-none rounded-e-lg", className)}
      ref={ref}
      {...props}
    />
  ),
);
InputComponent.displayName = "InputComponent";

const FlagComponent = ({
  country,
  countryName,
}: RPNInput.FlagProps): React.JSX.Element => {
  const Flag = flags[country];

  return (
    <span className='flex w-6 h-4 overflow-hidden rounded-sm bg-foreground/20'>
      {Flag && <Flag title={countryName} />}
    </span>
  );
};
FlagComponent.displayName = "FlagComponent";

type CountrySelectOption = { label: string; value: RPNInput.Country };

type CountrySelectProps = {
  disabled?: boolean;
  value: RPNInput.Country;
  onChange: (value: RPNInput.Country) => void;
  options: CountrySelectOption[];
  allowedCountries: RPNInput.Country[];
};

const CountrySelect = ({
  disabled,
  value,
  onChange,
  options,
  allowedCountries,
}: CountrySelectProps): React.JSX.Element => {
  // myCountry = value
  const handleSelect = React.useCallback(
    (country: RPNInput.Country) => {
      onChange(country);
      myCountry = country;
    },
    [onChange],
  );

  // Filter options to only include allowed countries
  const filteredOptions = options.filter((option) =>
    allowedCountries.includes(option.value),
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type='button'
          variant={"outline"}
          className={cn("flex gap-1 rounded-e-none rounded-s-lg pr-1 pl-3")}
          disabled={disabled || filteredOptions.length === 1}
        >
          <FlagComponent country={value} countryName={value} />
          <ChevronUp
            className={cn(
              "h-4 w-4 opacity-50",
              disabled ? "hidden" : "opacity-100",
            )}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent align='start' className='p-0'>
        <Command>
          <CommandList>
            <CommandGroup>
              {/* {options */}
              {filteredOptions
                ?.filter((x) => x.value)
                ?.map((option) => (
                  <CommandItem
                    className='gap-2'
                    key={option.value}
                    onSelect={() => handleSelect(option.value)}
                  >
                    <FlagComponent
                      country={option.value}
                      countryName={option.label}
                    />
                    <span className='flex-1 text-sm'>{option.label}</span>

                    {option.value && (
                      <span className='text-sm text-foreground/50'>
                        {`${RPNInput.getCountryCallingCode(option.value)}`}
                      </span>
                    )}
                    <CheckIcon
                      className={cn(
                        "ml-auto h-4 w-4",
                        option.value === value ? "opacity-100" : "opacity-0",
                      )}
                    />
                  </CommandItem>
                ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

type PhoneInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "onChange" | "value"
> &
  Omit<RPNInput.Props<typeof RPNInput.default>, "onChange"> & {
    onChange: (value: string) => void;
    value?: string;
    allowedCountries?: RPNInput.Country[];
  };

const PhoneInput: React.ForwardRefExoticComponent<PhoneInputProps> =
  React.forwardRef<React.ElementRef<typeof RPNInput.default>, PhoneInputProps>(
    ({ className, onChange, allowedCountries = ["AU"], ...props }, ref) => {
      const handleOnChange = React.useCallback(
        (value: RPNInput.Value) => {
          if (!value) {
            if (myCountry) {
              onChange(`+${RPNInput.getCountryCallingCode(myCountry)}`);
            }

            return;
          }

          if (myCountry) {
            // Formatting the phone number with a space after the country code
            const formattedValue = value.replace(
              `+${RPNInput.getCountryCallingCode(myCountry)}`,
              `+${RPNInput.getCountryCallingCode(myCountry)} `,
            );
            onChange(formattedValue);
          }
        },
        [onChange],
      );

      return (
        <RPNInput.default
          ref={ref}
          international
          defaultCountry='AU'
          smartCaret
          onFocus={() => {
            if (!myCountry) {
              myCountry = "AU";
            }
          }}
          limitMaxLength
          countryCallingCodeEditable={false}
          className={cn("flex", className)}
          flagComponent={FlagComponent}
          countrySelectComponent={CountrySelect}
          countrySelectProps={{
            allowedCountries,
          }}
          inputComponent={InputComponent}
          onChange={handleOnChange}
          {...props}
        />
      );
    },
  );
PhoneInput.displayName = "PhoneInput";

export { PhoneInput };
